import { useRef } from 'react';
import { useMotionEnabled } from '../../lib/motion';
import { useIdleTurns } from '../../lib/useIdleTurns';
import { useInViewActive } from '../../lib/useInViewActive';

/**
 * Section divider from the design: a glowing hairline capped with spark
 * diamonds, optionally split by the large center star ornament
 * (Break-line 2 in Figma).
 *
 * The spark art reaches its bounding-box edges, so a flush layout has the line
 * meeting the diamond at its thin inner point (which tapers to ~0px) — reading
 * as a gap. Instead the line runs UNDER the diamonds to their bright center via
 * a negative margin, with the diamonds layered on top (z-10), so the hairline
 * and sparks connect as one continuous horizontal unit.
 */
type BreakLineProps = {
  withCenterStar?: boolean;
  className?: string;
};

// Center-star turn choreography: dwell, then one smooth 90° turn in a random
// direction. The slight terminal overshoot gives the turn a mechanical snap.
const TURN_MS = 900;
const TURN_EASE = 'cubic-bezier(0.55, 0.05, 0.3, 1.15)';
const DWELL_MIN_MS = 1600;
const DWELL_RANGE_MS = 2400;
const QUARTER_TURN = () => (Math.random() < 0.5 ? -90 : 90);

/**
 * The large center ornament, idling with 90° turns in random directions
 * (the shared useIdleTurns rhythm; a CSS `transition` on `transform`
 * interpolates each step — GPU-composited and supported by effectively
 * every browser; anywhere it's unavailable the art simply stays put or
 * steps, never breaking layout: rotation doesn't affect the layout box and
 * the viewBox is square). The timer only runs while the ornament is
 * on-screen in a visible tab AND motion is enabled, so under
 * prefers-reduced-motion (or any client that suppresses animation) the
 * star is the design's static ornament.
 */
function CenterStar() {
  const ref = useRef<HTMLImageElement>(null);
  const motionOn = useMotionEnabled();
  const active = useInViewActive(ref) && motionOn;
  const angle = useIdleTurns(active, DWELL_MIN_MS, DWELL_RANGE_MS, QUARTER_TURN);

  return (
    <img
      ref={ref}
      src="/assets/images/ornaments/centerstar.svg"
      alt=""
      className="glow-white-md relative z-10 w-[clamp(4rem,10vw,7.5rem)] shrink-0"
      style={{
        transform: `rotate(${angle}deg)`,
        transition: `transform ${TURN_MS}ms ${TURN_EASE}`,
      }}
    />
  );
}

// The spark's rendered half-width along the line (span is w-[46px] → 23px to
// center). The line overlaps by just under that so it terminates at the node.
const OVERLAP = 22;

function EndStar() {
  // The spark art is portrait (27×45.77) — rotated 90° it lies along the
  // line inside a landscape box, as in the Figma frames.
  return (
    <span className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center">
      <img
        src="/assets/images/ornaments/star.svg"
        alt=""
        className="glow-white-md h-[46px] w-[27px] max-w-none rotate-90"
      />
    </span>
  );
}

function Line({ maxWidth }: { maxWidth: string }) {
  return (
    <div
      className="h-[2px] min-w-px flex-1 bg-[#8c8c8c] shadow-[0px_0px_20px_0px_rgba(255,255,255,0.5)]"
      // Negative inline margin pulls each end under the adjacent diamond's
      // center so the hairline visibly connects into the spark node.
      style={{ maxWidth, marginLeft: -OVERLAP, marginRight: -OVERLAP }}
    />
  );
}

export function BreakLine({ withCenterStar = false, className }: BreakLineProps) {
  return (
    <div
      aria-hidden="true"
      className={`flex w-full items-center justify-center px-5 py-[18px] ${className ?? ''}`}
    >
      <EndStar />
      <Line maxWidth={withCenterStar ? '500px' : '1200px'} />
      {withCenterStar && (
        <>
          <CenterStar />
          <Line maxWidth="500px" />
        </>
      )}
      <EndStar />
    </div>
  );
}
